import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Field } from '@elysian/ui';
import { friendlyAuthError, safeReturnTo } from '../../auth';
import { useSession } from '../../state/session';
import { AuthLayout } from './AuthLayout';

type Step = 'password' | 'code' | 'link_sent';

export function SignIn() {
  const { auth, authConfig, notice, refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!auth) return <AuthLayout title="Sign in"><p className="el-muted" style={{ margin: 0 }}>Loading…</p></AuthLayout>;

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try { await work(); } catch (e) { setError(friendlyAuthError(e)); } finally { setBusy(false); }
  }
  const finish = async () => { await refresh(); navigate(returnTo, { replace: true }); };

  if (auth.style === 'redirect') {
    return (
      <AuthLayout title="Sign in">
        {notice && <p className="el-hint" role="status" style={{ margin: 0 }}>{notice}</p>}
        {authConfig?.hint && <p className="el-muted" style={{ margin: 0 }}>{authConfig.hint}</p>}
        <Button variant="primary" disabled={busy} onClick={() => void run(() => auth.signInWithRedirect!(returnTo))}>{auth.label}</Button>
        {error && <div className="el-error-box" role="alert">{error}</div>}
      </AuthLayout>
    );
  }

  if (step === 'link_sent') {
    return (
      <AuthLayout title="Check your email">
        <p style={{ margin: 0 }}>If {email.trim()} has an account, a sign-in link is on its way. Open it on this device; it works once and expires within the hour.</p>
        <Button variant="ghost" onClick={() => setStep('password')}>Back to sign in</Button>
      </AuthLayout>
    );
  }

  if (step === 'code') {
    return (
      <AuthLayout title="Enter your code">
        <p className="el-muted" style={{ margin: 0 }}>Open your authenticator app and enter the 6-digit code for this account.</p>
        <form className="el-stack" style={{ gap: 14 }} onSubmit={(e) => { e.preventDefault(); void run(async () => { await auth.verifyCode!(code); await finish(); }); }}>
          <Field id="si-code" label="Code">
            <input id="si-code" className="el-input el-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} autoFocus
              value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))} />
          </Field>
          <Button type="submit" variant="primary" disabled={busy || code.replace(/\s/g, '').length !== 6}>Continue</Button>
        </form>
        {error && <div className="el-error-box" role="alert">{error}</div>}
      </AuthLayout>
    );
  }

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  return (
    <AuthLayout title="Sign in">
      {notice && <p className="el-hint" role="status" style={{ margin: 0 }}>{notice}</p>}
      {authConfig?.hint && <p className="el-muted" style={{ margin: 0 }}>{authConfig.hint}</p>}
      <form className="el-stack" style={{ gap: 14 }} onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          const r = await auth.signInWithPassword!(email, password);
          if (r.status === 'mfa_required') setStep('code'); else await finish();
        });
      }}>
        <Field id="si-email" label="Email">
          <input id="si-email" className="el-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="si-password" label="Password">
          <input id="si-password" className="el-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy || !emailOk || !password}>Sign in</Button>
      </form>
      {auth.sendSignInLink && (
        <>
          <div className="el-auth-or">or</div>
          <Button disabled={busy || !emailOk} onClick={() => void run(async () => { await auth.sendSignInLink!(email, returnTo); setStep('link_sent'); })}>
            Email me a sign-in link
          </Button>
          <p className="el-hint" style={{ margin: 0 }}>No password, or forgotten it? Type your email and we’ll send a link that signs you in.</p>
        </>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </AuthLayout>
  );
}
